import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError, API, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { PendingMembers } from "@/components/Franchise";
import { OwnerProfileButton, OwnerLedger } from "@/components/OwnerManage";
import PageHeader from "@/components/PageHeader";
import { CommissionSummary } from "@/components/CommissionSummary";
import { FullListDownload } from "@/components/FullListDownload";
import { WalletAdmin } from "@/components/WalletAdmin";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { GuideBookButton } from "@/components/GuideBookButton";
import { Crown, Plus, Pencil, Trash2, Users, TrendingUp, Copy, Share2, Camera, Percent, Landmark, QrCode, MessageCircle, FileDown, PhoneCall } from "lucide-react";

export default function Owners({ embedded = false }) {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const isAdmin = !!user?.is_admin;
  const token = localStorage.getItem("token");
  const [data, setData] = useState({ owners: [], net_profit: 0, total_revenue: 0, total_expense: 0, total_commission: 0 });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", percentage: "", details: "", phone: "" });
  const [photo, setPhoto] = useState(null);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [photoFor, setPhotoFor] = useState(null);
  const [imgV, setImgV] = useState(0);
  const fileRef = useRef(null);
  const cardFileRef = useRef(null);

  const uploadCardPhoto = async (file) => {
    if (!file || !photoFor) return;
    try {
      const fd = new FormData();
      fd.append("photo", file);
      await api.post(`/owners/${photoFor}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Profile photo updated");
      setImgV(Date.now()); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setPhotoFor(null); if (cardFileRef.current) cardFileRef.current.value = ""; }
  };

  const load = useCallback(async () => setData((await api.get("/owners")).data), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditId(null); setForm({ name: "", percentage: "", details: "", phone: "" }); setPhoto(null); setOpen(true); };
  const openEdit = (o) => { setEditId(o.id); setForm({ name: o.name, percentage: o.percentage, details: o.details || "", phone: o.phone || "" }); setPhoto(null); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Company partner name is required"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("workspace_id", localStorage.getItem("workspace_id"));
      fd.append("name", form.name);
      fd.append("percentage", form.percentage || 0);
      fd.append("details", form.details);
      fd.append("phone", form.phone);
      if (photo) fd.append("photo", photo);
      if (editId) await api.put(`/owners/${editId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      else await api.post("/owners", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(editId ? "Company partner updated" : "Company partner added");
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/owners/${id}`); load(); toast.success("Company partner removed"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const copy = (text, msg) => { navigator.clipboard.writeText(text); toast.success(msg); };
  const waShare = (link) => `https://wa.me/?text=${encodeURIComponent(`Assalamu Alaikum! Bengal Umrah House Travels invites you. Join or book your Umrah here: ${link}`)}`;

  return (
    <div>
      {!embedded && (
        <Link to="/telecaller" data-testid="owner-telecaller-shortcut"
          className="mb-6 flex items-center gap-4 p-4 rounded-xl border border-gold/40 bg-gold/10 hover:bg-gold/20 transition-colors gold-glow">
          <span className="w-12 h-12 rounded-full bg-gold text-[#0a2e22] flex items-center justify-center shrink-0"><PhoneCall className="w-6 h-6" /></span>
          <span className="flex-1">
            <span className="block font-serif text-lg text-gold">Telecaller Sheet — Next Call</span>
            <span className="block text-sm text-muted-foreground">Tap to open the calling list (Umrah & Job leads) and dial the next number</span>
          </span>
        </Link>
      )}
      {!embedded && <PageHeader eyebrow="Business" title="Company Panel"
        subtitle="Equity partners, their share of net profit, and personal referral links"
        action={isAdmin && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditId(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-owner-btn" onClick={openNew} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-2"><Plus className="w-4 h-4" /> Add Company Partner</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#05261D] border-gold/20">
              <DialogHeader>
                <DialogTitle className="font-serif text-gold flex items-center gap-2"><Crown className="w-5 h-5" /> {editId ? "Edit Company Partner" : "Add Company Partner"}</DialogTitle>
                <DialogDescription>Set the company partner's name, profit-share percentage, details and photo.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs text-muted-foreground">Company partner name</Label><Input data-testid="owner-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-card/60" placeholder="e.g. Haji Abdul Rahman" /></div>
                <div><Label className="text-xs text-muted-foreground">Profit share (%)</Label><Input data-testid="owner-pct-input" type="number" min="0" max="100" value={form.percentage} onChange={(e) => setForm({ ...form, percentage: e.target.value })} className="mt-1 bg-card/60" placeholder="e.g. 40" /></div>
                <div><Label className="text-xs text-muted-foreground">Mobile (optional)</Label><Input data-testid="owner-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 bg-card/60" placeholder="+91..." /></div>
                <div><Label className="text-xs text-muted-foreground">Details / role</Label><Textarea data-testid="owner-details-input" value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} className="mt-1 bg-card/60 min-h-[70px]" placeholder="Founding partner, finance, operations…" /></div>
                <div>
                  <Label className="text-xs text-muted-foreground">Profile photo</Label>
                  <Button type="button" variant="outline" data-testid="owner-photo-btn" onClick={() => fileRef.current?.click()} className="mt-1 w-full border-gold/40 text-gold gap-2"><Camera className="w-4 h-4" /> {photo ? photo.name : "Choose photo"}</Button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="save-owner-btn" onClick={save} disabled={saving || !form.name} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold">{saving ? "Saving…" : "Save Company Partner"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )} />}

      <input ref={cardFileRef} type="file" accept="image/*" className="hidden" data-testid="owner-card-photo-input" onChange={(e) => uploadCardPhoto(e.target.files?.[0])} />
      <div className={embedded ? "space-y-6" : "p-4 sm:p-8 space-y-6"}>
        {!embedded && isOwner && <WalletAdmin />}
        {!embedded && isOwner && <FullListDownload />}
        {!embedded && isOwner && <CommissionSummary />}
        <div className="flex items-center justify-between">
          <p className="eyebrow text-gold">Company Profit &amp; Loss</p>
          <Link to="/pnl" data-testid="owners-full-pnl-link" className="text-xs text-gold underline">View full P&amp;L</Link>
        </div>
        <div className="grid sm:grid-cols-4 gap-4" data-testid="owners-pnl-summary">
          <SummaryCard icon={TrendingUp} label="Total revenue" value={inr(data.total_revenue)} />
          <SummaryCard icon={TrendingUp} label="Total expense" value={inr(data.total_expense)} />
          <SummaryCard icon={Percent} label="Commissions distributed" value={inr(data.total_commission)} />
          <SummaryCard icon={Crown} label="Net profit" value={inr(data.net_profit)} highlight />
        </div>

        <div>
          <p className="eyebrow text-gold mb-3">Company Partners & Company Share (side by side)</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.owners.map((o) => (
              <Card key={o.id} data-testid={`owner-card-${o.id}`} className="p-5 bg-card/40 border-gold/30 card-hover flex flex-col">
                <div className="flex items-start gap-3">
                  <div className="relative flex-shrink-0">
                    {o.photo_path ? (
                      <img src={`${API}/owners/${o.id}/photo?auth=${token}&v=${imgV}`} alt={o.name} className="w-16 h-16 rounded-full object-cover ring-2 ring-gold/50" />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-primary/50 border border-gold/30 flex items-center justify-center text-xl font-serif text-gold">{o.name[0]}</div>
                    )}
                    {isOwner && (
                      <button type="button" data-testid={`owner-photo-upload-${o.id}`} title="Upload profile photo"
                        onClick={() => { setPhotoFor(o.id); cardFileRef.current?.click(); }}
                        className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-gold text-[#0a2e22] flex items-center justify-center shadow hover:scale-110 transition-transform">
                        <Camera className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif font-semibold text-white flex items-center gap-1.5"><Crown className="w-3.5 h-3.5 text-gold" /> {o.name}</p>
                    {o.phone && <p className="text-[11px] text-muted-foreground font-mono mt-1">{o.phone}</p>}
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" data-testid={`edit-owner-${o.id}`} onClick={() => openEdit(o)} className="h-8 w-8 text-gold hover:bg-gold/15"><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="icon" variant="ghost" data-testid={`delete-owner-${o.id}`} onClick={() => remove(o.id)} className="h-8 w-8 text-destructive hover:bg-destructive/15"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  )}
                </div>
                <div className="mt-4 rounded-xl bg-gradient-to-br from-gold/20 to-gold/5 border border-gold/40 py-4 text-center" data-testid={`owner-share-${o.id}`}>
                  <p className="eyebrow text-gold/80">Company Share</p>
                  <p className="text-4xl font-serif font-semibold text-gold leading-none mt-1">{o.percentage}%</p>
                  <p className="text-[11px] text-emerald-100/70 mt-2">Profit: <span className="text-gold font-mono">{inr(o.profit_share)}</span></p>
                </div>
                {o.details && <p className="text-xs text-muted-foreground mt-3 leading-relaxed">{o.details}</p>}
                <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-border/50 text-center">
                  <div><p className="eyebrow text-muted-foreground">Business</p><p className="text-base font-serif text-gold" data-testid={`owner-business-${o.id}`}>{inr(o.revenue || 0)}</p></div>
                  <div><p className="eyebrow text-muted-foreground">Customers</p><p className="text-base font-serif text-white">{o.customers}</p></div>
                  <div><p className="eyebrow text-muted-foreground">Members</p><p className="text-base font-serif text-white">{o.members}</p></div>
                </div>
                {isAdmin && (
                  <Button size="sm" variant="outline" data-testid={`owner-assign-${o.id}`}
                    onClick={async () => {
                      try {
                        const r = await api.post(`/owners/${o.id}/assign-existing`);
                        toast.success(`Assigned to ${o.name}: ${r.data.members_assigned} members, ${r.data.customers_assigned} customers`);
                        load();
                      } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
                    }}
                    className="mt-3 w-full border-gold/40 text-gold text-[10px] h-7 gap-1"><Users className="w-3 h-3" /> Assign existing members &amp; customers</Button>
                )}
                {isOwner && <OwnerProfileButton o={o} token={token} onSaved={load} />}
                {isOwner && <OwnerLedger o={o} />}
                <a data-testid={`owner-prospectus-${o.id}`} href={`${API}/prospectus/member.pdf?ref=${o.referral_code}&origin=${encodeURIComponent(window.location.origin)}`} target="_blank" rel="noopener noreferrer"
                  className="mt-3 w-full inline-flex items-center justify-center gap-1 h-7 rounded-md border border-gold/40 text-gold text-[10px] hover:bg-gold/10 transition-colors"><FileDown className="w-3 h-3" /> Download {o.name}'s prospectus</a>
                <div className="mt-3 flex justify-center"><GuideBookButton testid={`owner-guide-${o.id}`} small href={`${API}/prospectus/guide.pdf?ref=${o.referral_code}&origin=${encodeURIComponent(window.location.origin)}`} /></div>
                <div className="mt-4 space-y-2">
                  <p className="eyebrow text-muted-foreground flex items-center gap-1.5"><Share2 className="w-3.5 h-3.5 text-gold" /> {o.name}'s share links</p>
                  <div className="flex items-center gap-2">
                    <Input readOnly value={`${window.location.origin}/portal?ref=${o.referral_code}`} className="bg-card/60 font-mono text-[10px] h-8" />
                    <Button size="sm" type="button" data-testid={`owner-cust-link-${o.id}`} onClick={() => copy(`${window.location.origin}/portal?ref=${o.referral_code}`, "Customer link copied")} className="bg-gold text-[#0a2e22] hover:bg-gold/90 text-[10px] h-8 gap-1 flex-shrink-0"><Copy className="w-3 h-3" /> Customer</Button>
                    <a data-testid={`owner-cust-wa-${o.id}`} href={waShare(`${window.location.origin}/portal?ref=${o.referral_code}`, o.name)} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-md bg-[#25D366]/15 text-[#25D366] border border-[#25D366]/40 hover:bg-[#25D366]/25 transition-colors" title="Share customer link on WhatsApp"><MessageCircle className="w-3.5 h-3.5" /></a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input readOnly value={`${window.location.origin}/agent?ref=${o.referral_code}`} className="bg-card/60 font-mono text-[10px] h-8" />
                    <Button size="sm" type="button" data-testid={`owner-member-link-${o.id}`} onClick={() => copy(`${window.location.origin}/agent?ref=${o.referral_code}`, "Member link copied")} className="bg-gold text-[#0a2e22] hover:bg-gold/90 text-[10px] h-8 gap-1 flex-shrink-0"><Copy className="w-3 h-3" /> Member</Button>
                    <a data-testid={`owner-member-wa-${o.id}`} href={waShare(`${window.location.origin}/agent?ref=${o.referral_code}`, o.name)} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-md bg-[#25D366]/15 text-[#25D366] border border-[#25D366]/40 hover:bg-[#25D366]/25 transition-colors" title="Share member link on WhatsApp"><MessageCircle className="w-3.5 h-3.5" /></a>
                  </div>
                </div>
              </Card>
            ))}
            {data.owners.length === 0 && (
              <Card className="p-8 bg-card/40 border-border/60 col-span-full text-center">
                <Crown className="w-8 h-8 text-gold mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No company partners added yet. {isAdmin ? "Click “Add Company Partner” to create the first equity partner." : "Ask the admin to add company partners."}</p>
              </Card>
            )}
          </div>
        </div>
        {isOwner && !embedded && <PendingMembers />}
        {isOwner && !embedded && <PaymentReceipts token={token} />}
        {isOwner && !embedded && <PaymentSettings />}
        <Card className="p-4 bg-card/30 border-border/50">
          <p className="text-xs text-muted-foreground flex items-start gap-2"><Users className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
            All company partners sign in with the same company email & password and see every profile here side by side. Each partner's share is their percentage of the net profit (revenue − expenses − commissions). Their personal referral links credit new customers and members to them automatically. Only the admin can add, edit or delete company partners.</p>
        </Card>
      </div>
    </div>
  );
}

function PaymentReceipts({ token }) {
  const [subs, setSubs] = useState([]);
  const load = useCallback(async () => setSubs((await api.get("/payment-submissions")).data), []);
  useEffect(() => { load(); }, [load]);
  const act = async (id, action) => {
    try { await api.post(`/payment-submissions/${id}/${action}`); toast.success(action === "accept" ? "Payment accepted — added to customer balance" : "Rejected"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  return (
    <Card className="p-5 bg-card/40 border-gold/30" data-testid="payment-receipts-card">
      <p className="font-serif font-semibold text-white flex items-center gap-2 mb-3"><Landmark className="w-4 h-4 text-gold" /> Payment Receipts to Review</p>
      {subs.length === 0 && <p className="text-sm text-muted-foreground">No payment receipts submitted yet.</p>}
      <div className="space-y-2">
        {subs.map((s) => (
          <div key={s.id} data-testid={`receipt-${s.id}`} className="flex flex-wrap items-center justify-between gap-2 border-t border-border/40 pt-2">
            <div className="min-w-0">
              <p className="text-sm text-white">{s.payer_name} · <span className="font-mono text-gold">{inr(s.amount)}</span></p>
              <p className="text-[10px] text-muted-foreground">{s.note || "—"} · {new Date(s.created_at).toLocaleDateString("en-IN")}</p>
            </div>
            <div className="flex items-center gap-2">
              {s.has_receipt && <a href={`${API}/payment-submissions/${s.id}/receipt?auth=${token}`} target="_blank" rel="noopener noreferrer" className="text-[11px] text-gold underline">View receipt</a>}
              {s.status === "pending" ? (
                <>
                  <Button size="sm" data-testid={`receipt-accept-${s.id}`} onClick={() => act(s.id, "accept")} className="bg-gold text-[#0a2e22] hover:bg-gold/90 text-[10px] h-7">Accept</Button>
                  <Button size="sm" variant="outline" data-testid={`receipt-reject-${s.id}`} onClick={() => act(s.id, "reject")} className="border-destructive/40 text-destructive text-[10px] h-7">Reject</Button>
                </>
              ) : (
                <Badge variant="outline" className={s.status === "accepted" ? "border-emerald-500/40 text-emerald-300" : "border-destructive/40 text-destructive"}>{s.status}</Badge>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function PaymentSettings() {
  const [info, setInfo] = useState({ account_name: "", bank_name: "", account_number: "", ifsc: "", upi_id: "" });
  const [qr, setQr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasQr, setHasQr] = useState(false);
  const qrRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/payment-info");
    setInfo({ account_name: data.account_name || "", bank_name: data.bank_name || "", account_number: data.account_number || "", ifsc: data.ifsc || "", upi_id: data.upi_id || "" });
    setHasQr(!!data.qr_path);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/payment-info", info);
      if (qr) {
        const fd = new FormData();
        fd.append("photo", qr);
        await api.post("/payment-info/qr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Payment details saved — shown at package & franchise purchase");
      setQr(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Card className="p-5 bg-card/40 border-gold/30" data-testid="payment-settings-card">
      <p className="font-serif font-semibold text-white flex items-center gap-2 mb-1"><Landmark className="w-4 h-4 text-gold" /> Company Payment Details</p>
      <p className="text-[11px] text-muted-foreground mb-4">Bank account &amp; payment QR shown to customers when they buy a package or franchise.</p>
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="space-y-3">
          <div><Label className="text-xs text-muted-foreground">Account holder name</Label><Input data-testid="pay-account-name" value={info.account_name} onChange={(e) => setInfo({ ...info, account_name: e.target.value })} className="mt-1 bg-card/60" placeholder="Bengal Umrah House" /></div>
          <div><Label className="text-xs text-muted-foreground">Bank name</Label><Input data-testid="pay-bank-name" value={info.bank_name} onChange={(e) => setInfo({ ...info, bank_name: e.target.value })} className="mt-1 bg-card/60" placeholder="State Bank of India" /></div>
          <div><Label className="text-xs text-muted-foreground">Account number</Label><Input data-testid="pay-account-number" value={info.account_number} onChange={(e) => setInfo({ ...info, account_number: e.target.value })} className="mt-1 bg-card/60 font-mono" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-muted-foreground">IFSC</Label><Input data-testid="pay-ifsc" value={info.ifsc} onChange={(e) => setInfo({ ...info, ifsc: e.target.value })} className="mt-1 bg-card/60 font-mono" /></div>
            <div><Label className="text-xs text-muted-foreground">UPI ID</Label><Input data-testid="pay-upi" value={info.upi_id} onChange={(e) => setInfo({ ...info, upi_id: e.target.value })} className="mt-1 bg-card/60 font-mono" placeholder="bengalbumrah@upi" /></div>
          </div>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground flex items-center gap-1.5"><QrCode className="w-3.5 h-3.5 text-gold" /> Payment QR / Barcode</Label>
          <div className="mt-2 flex items-center gap-4">
            {hasQr && !qr && <img src={`${API}/public/payment-info/qr`} alt="Payment QR" className="w-28 h-28 rounded-xl border border-gold/40 object-cover" />}
            {qr && <img src={URL.createObjectURL(qr)} alt="QR preview" className="w-28 h-28 rounded-xl border border-gold/40 object-cover" />}
            <Button type="button" variant="outline" data-testid="pay-qr-btn" onClick={() => qrRef.current?.click()} className="border-gold/40 text-gold gap-2">
              <QrCode className="w-4 h-4" /> {qr ? qr.name : hasQr ? "Change QR" : "Upload QR"}
            </Button>
            <input ref={qrRef} type="file" accept="image/*" className="hidden" onChange={(e) => setQr(e.target.files?.[0] || null)} />
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        <Button data-testid="pay-save-btn" onClick={save} disabled={saving} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold">{saving ? "Saving…" : "Save Payment Details"}</Button>
        <Button variant="outline" data-testid="pay-clear-btn" disabled={saving}
          onClick={async () => { try { await api.delete("/payment-info"); toast.success("Bank details deleted"); load(); } catch (err) { toast.error(apiError(err.response?.data?.detail)); } }}
          className="border-destructive/40 text-destructive">Delete bank details</Button>
        {hasQr && (
          <Button variant="outline" data-testid="pay-qr-delete-btn" disabled={saving}
            onClick={async () => { try { await api.delete("/payment-info/qr"); toast.success("QR deleted"); load(); } catch (err) { toast.error(apiError(err.response?.data?.detail)); } }}
            className="border-destructive/40 text-destructive">Delete QR</Button>
        )}
      </div>
    </Card>
  );
}

function SummaryCard({ icon: Icon, label, value, highlight }) {
  return (
    <Card className={`p-4 bg-card/40 border-border/60 ${highlight ? "border-gold/40 gold-glow" : ""}`}>
      <div className="flex items-center gap-2 mb-2 text-gold"><Icon className="w-4 h-4" /><span className="eyebrow text-muted-foreground">{label}</span></div>
      <p className={`text-2xl font-serif font-semibold ${highlight ? "text-gold" : "text-white"}`}>{value}</p>
    </Card>
  );
}
